import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../api/client';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Search, FileText, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_TABS = [
  { key: 'all', label: '전체' },
  { key: 'draft', label: 'Draft' },
  { key: 'completed', label: 'Completed' },
  { key: 'shared', label: 'Shared' },
];

const STATUS_STYLE = {
  draft: 'bg-gray-100 text-gray-700 border-gray-300',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  shared: 'bg-blue-50 text-blue-700 border-blue-200',
};

const STATUS_LABEL = { draft: 'Draft', completed: 'Completed', shared: 'Shared' };

const fmtDate = (s) => {
  if (!s) return '-';
  const d = new Date(s.length === 10 ? s + 'T00:00:00' : s);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' });
};

export default function FitReports() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusTab, setStatusTab] = useState('all');
  const [query, setQuery] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusTab !== 'all') params.status = statusTab;
      if (query.trim()) params.q = query.trim();
      const res = await apiClient.get('/admin/fit-reports', { params });
      setReports(res.data || []);
    } catch (e) {
      console.error(e);
      toast.error('리포트 목록을 불러오지 못했습니다');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusTab]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const counts = useMemo(() => {
    const c = { all: reports.length, draft: 0, completed: 0, shared: 0 };
    reports.forEach((r) => {
      if (c[r.status] !== undefined) c[r.status] += 1;
    });
    return c;
  }, [reports]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`'${name}'님의 FIT REPORT를 삭제하시겠습니까?`)) return;
    try {
      await apiClient.delete(`/admin/fit-reports/${id}`);
      toast.success('리포트가 삭제되었습니다');
      load();
    } catch (e) {
      toast.error('삭제에 실패했습니다');
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="fit-reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="w-6 h-6 text-orange-500" />
            FIT REPORT
          </h1>
          <p className="text-sm text-gray-500 mt-1">상담용 점수 리포트 — 회원별 측정 결과를 박제하여 보관</p>
        </div>
        <Button
          onClick={() => navigate('/admin/fit-reports/new')}
          data-testid="btn-create-report"
          className="bg-orange-500 hover:bg-orange-600 text-white"
        >
          <Plus className="w-4 h-4 mr-1.5" /> REPORT 생성
        </Button>
      </div>

      {/* Search + Filter */}
      <Card>
        <CardContent className="pt-5 pb-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="회원명 또는 전화번호로 검색"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-9"
              data-testid="input-search-reports"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setStatusTab(tab.key)}
                data-testid={`filter-${tab.key}`}
                className={
                  'px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ' +
                  (statusTab === tab.key
                    ? 'bg-orange-500 text-white border-orange-500'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300')
                }
              >
                {tab.label}
                {statusTab === 'all' && tab.key === 'all' && (
                  <span className="ml-1.5 text-xs opacity-80">({counts.all})</span>
                )}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-16 flex justify-center items-center text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> 불러오는 중...
            </div>
          ) : reports.length === 0 ? (
            <div className="py-16 text-center text-gray-400" data-testid="empty-state">
              <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>아직 생성된 리포트가 없습니다.</p>
              <p className="text-sm mt-1">상단의 <b>REPORT 생성</b> 버튼을 눌러 첫 리포트를 만들어보세요.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">회원명</th>
                    <th className="text-left px-4 py-3 font-medium">전화번호</th>
                    <th className="text-left px-4 py-3 font-medium">Report Date</th>
                    <th className="text-right px-4 py-3 font-medium">종합점수</th>
                    <th className="text-left px-4 py-3 font-medium">Level</th>
                    <th className="text-left px-4 py-3 font-medium">상태</th>
                    <th className="text-right px-4 py-3 font-medium">동작</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {reports.map((r) => (
                    <tr
                      key={r._id}
                      className="hover:bg-orange-50/40 cursor-pointer transition-colors"
                      onClick={() => navigate(`/admin/fit-reports/${r._id}`)}
                      data-testid={`row-report-${r._id}`}
                    >
                      <td className="px-4 py-3 font-medium text-gray-900">{r.member_snapshot?.name || '-'}</td>
                      <td className="px-4 py-3 text-gray-600">{r.member_snapshot?.phone || '-'}</td>
                      <td className="px-4 py-3 text-gray-600">{fmtDate(r.report_date)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900">
                        {r.score_snapshot?.total_score ?? '-'}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{r.score_snapshot?.level || '-'}</td>
                      <td className="px-4 py-3">
                        <span
                          className={
                            'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ' +
                            (STATUS_STYLE[r.status] || STATUS_STYLE.draft)
                          }
                        >
                          {STATUS_LABEL[r.status] || r.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/fit-reports/${r._id}`);
                            }}
                            data-testid={`btn-view-${r._id}`}
                          >
                            보기
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(r._id, r.member_snapshot?.name || '');
                            }}
                            data-testid={`btn-delete-${r._id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
